import { Button, Flex } from "@chakra-ui/react";
import React from "react";
import { RiAddLine } from "react-icons/ri";
import { ParentGuardianSelectInput } from "src/components/Inputs/ParentGuardianSelectInput";
import { Glossary } from "src/components/Text/Glossary";
import type { ParentSummary } from "src/generated/hooks/form-service/requests";
import { isValidEmail } from "src/services/formValidations";
import { GuardianChip } from "./GuardianChip";
import { NewGuardianForm } from "./NewGuardianForm";
import type { GuardianEntry, NewGuardianEntry } from "./useGuardianEntries";

export interface ParentGuardianSectionProps {
  organizationId: string;
  entries: GuardianEntry[];
  // Name of the student the guardians belong to, for a new guardian's
  // "Parent of {student}" line.
  studentName: string;
  onAddExisting: (parent: ParentSummary) => void;
  onAddDraft: (prefill: {
    emailAddress?: string | null;
    phoneNumber?: string | null;
  }) => void;
  onUpdateDraft: (id: string, patch: Partial<NewGuardianEntry>) => void;
  // Collapse a new draft into a read-only chip (still in the payload).
  onConfirmDraft: (id: string) => void;
  onRemove: (id: string) => void;
  // Guardian ids already linked to the student, hidden from search results.
  excludeIds?: string[];
  disablePortal?: boolean;
}

type AddMode = "search" | "idle";

export function ParentGuardianSection({
  organizationId,
  entries,
  studentName,
  onAddExisting,
  onAddDraft,
  onUpdateDraft,
  onConfirmDraft,
  onRemove,
  excludeIds,
  disablePortal,
}: ParentGuardianSectionProps) {
  const [mode, setMode] = React.useState<AddMode>("search");

  // People already linked to the student (excludeIds) plus any already added in
  // this session. Shown disabled in search, never hidden, so they can't be re-added.
  const linkedIds = React.useMemo(
    () => [
      ...(excludeIds ?? []),
      ...entries
        .filter((entry) => entry.kind === "existing")
        .map((entry) => entry.id),
    ],
    [excludeIds, entries]
  );

  const handleSelectExisting = (parent: ParentSummary) => {
    onAddExisting(parent);
    setMode("idle");
  };

  const handleCreateNew = (searchValue: string) => {
    const isEmail = isValidEmail(searchValue);
    onAddDraft({
      emailAddress: isEmail ? searchValue : null,
      phoneNumber: isEmail ? null : searchValue || null,
    });
    setMode("idle");
  };

  return (
    <Flex direction="column" gap={4} width="100%">
      {entries.map((entry) =>
        entry.kind === "new" && !entry.confirmed ? (
          <NewGuardianForm
            key={entry.id}
            entry={entry}
            onChange={(patch) => onUpdateDraft(entry.id, patch)}
            onConfirm={() => onConfirmDraft(entry.id)}
            onRemove={() => onRemove(entry.id)}
          />
        ) : (
          <GuardianChip
            key={entry.id}
            entry={entry}
            studentName={studentName}
            onRemove={onRemove}
          />
        )
      )}

      {mode === "search" && (
        <ParentGuardianSelectInput
          organizationId={organizationId}
          excludeIds={linkedIds}
          disablePortal={disablePortal}
          onSelectedParent={(parent) => {
            if (parent) handleSelectExisting(parent);
          }}
          onCreateNew={handleCreateNew}
        />
      )}

      {mode === "idle" && (
        <Button
          type="button"
          variant="outline"
          colorScheme="gray"
          alignSelf="flex-start"
          leftIcon={<RiAddLine />}
          onClick={() => setMode("search")}
        >
          <Glossary>Add another parent/guardian</Glossary>
        </Button>
      )}
    </Flex>
  );
}
